import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";

const emailSchema = z.object({
  email: z.string()
    .trim()
    .email("Invalid email address")
    .max(255, "Email must be less than 255 characters")
    .toLowerCase()
});

export const CTA = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    const validation = emailSchema.safeParse({ email });
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    setLoading(true);

    // Use sanitized/validated email
    const sanitizedEmail = validation.data.email;

    const { error } = await supabase.functions.invoke('join-waitlist', {
      body: { email: sanitizedEmail }
    });

    setLoading(false);

    if (error) {
      toast.error('Something went wrong. Please try again.');
    } else {
      setSubmitted(true);
      toast.success("You're on the list! Check your email.");
      setEmail('');
    }
  };
  
  return (
    <section className="py-24 bg-background">
      <div className="container px-4">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-hero p-12 md:p-16 lg:p-20 shadow-elevated">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-primary-glow/30 via-transparent to-transparent" />
          
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-primary-foreground">
              Join the waitlist for early access
            </h2>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8">
              Be among the first to experience AI-powered contact management in Outlook.
            </p>
            
            {submitted ? (
              <div className="bg-primary-foreground/10 rounded-lg p-6 mb-6">
                <p className="text-primary-foreground text-lg">
                  ✅ You're on the list! Check your email for confirmation.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="max-w-md mx-auto mb-6">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input 
                    type="email" 
                    placeholder="Enter your email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="flex-1 bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/50"
                  />
                  <Button 
                    type="submit" 
                    size="lg" 
                    variant="hero"
                    disabled={loading}
                  >
                    {loading ? 'Joining...' : 'Join Waitlist'}
                  </Button>
                </div>
                <p className="text-xs text-primary-foreground/70 mt-3 text-center">
                  By joining the waitlist, you agree to our processing of your email address to send you product updates and early access information. You can unsubscribe at any time. See our <Link to="/privacy" className="text-primary-foreground underline">Privacy Policy</Link> for details.
                </p>
              </form>
            )}
            
            <div className="flex justify-center">
              <Button size="lg" variant="ghost" asChild className="border border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:border-primary-foreground/50">
                <Link to="/support">Talk to sales</Link>
              </Button>
            </div>
            
            <p className="mt-8 text-sm text-primary-foreground/60">
              Get early access and exclusive benefits at launch.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
